import posthog from "posthog-js";
import { getSessionReplayUrl } from "@/lib/error-tracking";
import { readLastEventId } from "@/lib/error-tracking/last-event-id";
import { isUnknownRecord, readUnknownProperty } from "@shared/validation/narrow";
import { requestIdFromServerTiming } from "@shared/telemetry/request-correlation";
import {
  readRecentConvexFailures,
  readRecentConvexRequestIds,
} from "@/lib/telemetry/convex-request-ids";
import { readDocumentServerTiming } from "@/lib/telemetry/document-server-timing";
import { redactPathCredential, redactUrlCredentials } from "@shared/security/redact-url";
import { publicConfigValue } from "@/lib/public-config";

export interface ClientDiagnostics {
  capturedAt: string;
  environment?: string;
  url?: string;
  path?: string;
  referrer?: string;
  documentTitle?: string;
  posthogReplayUrl?: string;
  posthogSessionId?: string;
  posthogDistinctId?: string;
  errorEventId?: string;
  landingRequestId?: string;
  convexRequestIds?: string;
  userAgent?: string;
  language?: string;
  languages?: string;
  timezone?: string;
  viewport?: string;
  screen?: string;
  connection?: string;
  deviceMemory?: string;
  cpuCores?: number;
  online?: boolean;
  visibility?: string;
}

interface NavigatorConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

function readNavigatorConnection(value: unknown): NavigatorConnection | undefined {
  if (!isUnknownRecord(value)) return undefined;
  return {
    effectiveType:
      typeof value.effectiveType === "string" ? value.effectiveType : undefined,
    downlink: typeof value.downlink === "number" ? value.downlink : undefined,
    rtt: typeof value.rtt === "number" ? value.rtt : undefined,
    saveData: typeof value.saveData === "boolean" ? value.saveData : undefined,
  };
}

function readString(getter: () => unknown): string | undefined {
  try {
    const value = getter();
    return typeof value === "string" && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function formatConnection(connection?: NavigatorConnection): string | undefined {
  if (!connection) return undefined;
  const parts: Array<string> = [];
  if (connection.effectiveType) parts.push(connection.effectiveType);
  if (typeof connection.downlink === "number")
    parts.push(`${connection.downlink} Mbps`);
  if (typeof connection.rtt === "number") parts.push(`rtt ${connection.rtt}ms`);
  if (connection.saveData) parts.push("save-data");
  return parts.length > 0 ? parts.join(", ") : undefined;
}

export function supportDiagnosticsFields(
  diagnostics: ClientDiagnostics = gatherClientDiagnostics(),
) {
  return {
    posthogSessionId: diagnostics.posthogSessionId,
    posthogReplayUrl: diagnostics.posthogReplayUrl,
    posthogDistinctId: diagnostics.posthogDistinctId,
    errorEventId: diagnostics.errorEventId,
    userAgent: diagnostics.userAgent,
    clientContext: supportClientContext(diagnostics),
  };
}

export function bugReportDiagnostics() {
  const diagnostics = gatherClientDiagnostics();
  return { ...supportDiagnosticsFields(diagnostics), pagePath: diagnostics.path };
}

function supportClientContext(diagnostics: ClientDiagnostics) {
  const convexFailures =
    typeof window === "undefined" ? [] : [...readRecentConvexFailures()];
  return {
    release: process.env.NEXT_PUBLIC_RELEASE_SHA || undefined,
    environment: diagnostics.environment,
    landingRequestId: diagnostics.landingRequestId,
    convexFailures: convexFailures.length > 0 ? convexFailures : undefined,
    viewport: diagnostics.viewport,
    timezone: diagnostics.timezone,
    language: diagnostics.language,
  };
}

function readCorrelationDiagnostics() {
  const convexRequestIds = readRecentConvexRequestIds();
  return {
    landingRequestId: requestIdFromServerTiming(readDocumentServerTiming()),
    convexRequestIds:
      convexRequestIds.length > 0 ? convexRequestIds.join(", ") : undefined,
  };
}

function readPageDiagnostics() {
  return {
    url: redactUrlCredentials(window.location.href),
    path: redactPathCredential(window.location.pathname),
    referrer: document.referrer ? redactUrlCredentials(document.referrer) : undefined,
    documentTitle: document.title || undefined,
    posthogReplayUrl: getSessionReplayUrl(),
    posthogSessionId: readString(() =>
      typeof posthog.get_session_id === "function" ? posthog.get_session_id() : undefined,
    ),
    posthogDistinctId: readString(() =>
      typeof posthog.get_distinct_id === "function" ? posthog.get_distinct_id() : undefined,
    ),
    errorEventId: readLastEventId() ?? undefined,
  };
}

function readDeviceDiagnostics() {
  const connection = readNavigatorConnection(readUnknownProperty(navigator, "connection"));
  const deviceMemory = readUnknownProperty(navigator, "deviceMemory");
  return {
    userAgent: navigator.userAgent || undefined,
    language: navigator.language || undefined,
    languages: navigator.languages?.length ? navigator.languages.join(", ") : undefined,
    timezone: readString(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    screen: `${window.screen.width}×${window.screen.height} @${window.devicePixelRatio}x`,
    connection: formatConnection(connection),
    deviceMemory: typeof deviceMemory === "number" ? `${deviceMemory} GB` : undefined,
    cpuCores:
      typeof navigator.hardwareConcurrency === "number" ? navigator.hardwareConcurrency : undefined,
    online: typeof navigator.onLine === "boolean" ? navigator.onLine : undefined,
    visibility: document.visibilityState,
  };
}

export function gatherClientDiagnostics(): ClientDiagnostics {
  const capturedAt = new Date().toISOString();
  const environment = publicConfigValue("NEXT_PUBLIC_ENVIRONMENT");
  if (typeof window === "undefined") return { capturedAt, environment };
  return {
    capturedAt,
    environment,
    ...readPageDiagnostics(),
    ...readCorrelationDiagnostics(),
    ...readDeviceDiagnostics(),
  };
}

interface DiagnosticsErrorInfo {
  message?: string;
  digest?: string;
  stack?: string;
}

function pushField(lines: Array<string>, label: string, value?: string | number | boolean) {
  if (value === undefined || value === "") return;
  lines.push(`**${label}:** ${value}`);
}

export function formatDiagnosticsMarkdown(
  diagnostics: ClientDiagnostics,
  errorInfo?: DiagnosticsErrorInfo,
): string {
  const lines: Array<string> = ["## Error diagnostics", ""];

  pushField(lines, "Captured", diagnostics.capturedAt);
  pushField(lines, "Environment", diagnostics.environment);

  if (errorInfo?.message || errorInfo?.digest || errorInfo?.stack) {
    lines.push("", "### Error");
    pushField(lines, "Message", errorInfo.message);
    pushField(lines, "Digest", errorInfo.digest);
    if (errorInfo.stack) lines.push("", "```", errorInfo.stack, "```");
  }

  lines.push("", "### Page");
  pushField(lines, "URL", diagnostics.url);
  pushField(lines, "Path", diagnostics.path);
  pushField(lines, "Referrer", diagnostics.referrer);
  pushField(lines, "Title", diagnostics.documentTitle);

  lines.push("", "### Session");
  pushField(lines, "PostHog replay", diagnostics.posthogReplayUrl);
  pushField(lines, "PostHog session", diagnostics.posthogSessionId);
  pushField(lines, "PostHog distinct id", diagnostics.posthogDistinctId);
  pushField(lines, "error event", diagnostics.errorEventId);
  pushField(lines, "Landing request id (ray)", diagnostics.landingRequestId);
  pushField(lines, "Recent Convex request ids", diagnostics.convexRequestIds);

  lines.push("", "### Client");
  pushField(lines, "User agent", diagnostics.userAgent);
  pushField(lines, "Viewport", diagnostics.viewport);
  pushField(lines, "Screen", diagnostics.screen);
  pushField(lines, "Language", diagnostics.language);
  pushField(lines, "Languages", diagnostics.languages);
  pushField(lines, "Timezone", diagnostics.timezone);
  pushField(lines, "Connection", diagnostics.connection);
  pushField(lines, "Device memory", diagnostics.deviceMemory);
  pushField(lines, "CPU cores", diagnostics.cpuCores);
  pushField(lines, "Online", diagnostics.online);
  pushField(lines, "Visibility", diagnostics.visibility);

  return lines.join("\n");
}
